import { InvariantViolation } from './errors';
import {
  ProfilePurpose,
  type ChargingSchedule,
  type ProfileKind,
  type RecurrencyKind,
} from './types';
import type { ChargingProfileId, TransactionId } from './types';

/**
 * connectorId is plain number because 0 means "station-wide" — outside the
 * positive-integer range that ConnectorId.of() enforces.
 */
export interface ChargingProfileSnapshot {
  readonly id: ChargingProfileId;
  readonly stackLevel: number;
  readonly purpose: ProfilePurpose;
  readonly kind: ProfileKind;
  readonly recurrencyKind?: RecurrencyKind;
  readonly validFrom?: number;
  readonly validTo?: number;
  readonly transactionId?: TransactionId;
  readonly connectorId: number;
  readonly schedule: ChargingSchedule;
}

export class ChargingProfile {
  readonly id: ChargingProfileId;
  readonly stackLevel: number;
  readonly purpose: ProfilePurpose;
  readonly kind: ProfileKind;
  readonly recurrencyKind: RecurrencyKind | null;
  readonly validFrom: number | null;
  readonly validTo: number | null;
  readonly transactionId: TransactionId | null;
  readonly connectorId: number;
  readonly schedule: ChargingSchedule;

  constructor(snap: ChargingProfileSnapshot) {
    if (snap.stackLevel < 0) {
      throw new InvariantViolation('ChargingProfile', 'stackLevel >= 0', `profileId=${snap.id}`);
    }
    if (snap.connectorId < 0) {
      throw new InvariantViolation('ChargingProfile', 'connectorId >= 0', `profileId=${snap.id}`);
    }
    if (snap.purpose === ProfilePurpose.TxProfile && snap.transactionId === undefined) {
      throw new InvariantViolation(
        'ChargingProfile',
        'TxProfile must reference a transactionId',
        `profileId=${snap.id}`,
      );
    }
    if (snap.kind === 'Recurring' && snap.recurrencyKind === undefined) {
      throw new InvariantViolation(
        'ChargingProfile',
        'Recurring kind must specify recurrencyKind',
        `profileId=${snap.id}`,
      );
    }
    if (snap.schedule.chargingSchedulePeriod.length === 0) {
      throw new InvariantViolation(
        'ChargingProfile',
        'schedule.chargingSchedulePeriod is non-empty',
        `profileId=${snap.id}`,
      );
    }
    if (
      snap.validFrom !== undefined &&
      snap.validTo !== undefined &&
      snap.validFrom > snap.validTo
    ) {
      throw new InvariantViolation(
        'ChargingProfile',
        'validFrom <= validTo',
        `profileId=${snap.id}`,
      );
    }

    this.id = snap.id;
    this.stackLevel = snap.stackLevel;
    this.purpose = snap.purpose;
    this.kind = snap.kind;
    this.recurrencyKind = snap.recurrencyKind ?? null;
    this.validFrom = snap.validFrom ?? null;
    this.validTo = snap.validTo ?? null;
    this.transactionId = snap.transactionId ?? null;
    this.connectorId = snap.connectorId;
    this.schedule = snap.schedule;
  }

  get isStationWide(): boolean {
    return this.connectorId === 0;
  }
}
